import React, { useEffect, useRef } from 'react';

/**
 * 確保「一次只有一個 popover 開啟」的互斥機制。
 * 每個 popover 元件在 mount 時呼叫此 hook：
 * - 廣播 `jodoni:popover:open` 事件，其他已開啟的 popover 收到後會自動 close
 * - 監聽相同事件；若是其他 popover 開的，本元件就 close
 */
export function useExclusivePopover(onClose: () => void) {
  const idRef = useRef<string>('');
  if (!idRef.current) {
    idRef.current = `popover-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  }
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const myId = idRef.current;
    // 監聽（先註冊，這樣 dispatchEvent 後其他 popover 才收得到、且自己也不會誤關自己）
    const handler = (e: Event) => {
      const openedId = (e as CustomEvent<string>).detail;
      if (openedId !== myId) onCloseRef.current();
    };
    window.addEventListener('jodoni:popover:open', handler);
    // 廣播自己開了 → 其他 popover 收到 detail !== myId 會關閉
    window.dispatchEvent(new CustomEvent('jodoni:popover:open', { detail: myId }));
    return () => window.removeEventListener('jodoni:popover:open', handler);
  }, []);
}

/** Wrapper：對 inline JSX 套用互斥行為（避免 .ts 內 JSX 限制，用 createElement） */
export const ExclusivePopover: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => {
  useExclusivePopover(onClose);
  return React.createElement(React.Fragment, null, children);
};
